import React, { useState } from 'react';
import { format } from 'date-fns';
import { Registration } from '../../api/client';
import { 
  CheckIcon, XMarkIcon, TrashIcon, PencilIcon, ClockIcon,
  EnvelopeIcon, CheckBadgeIcon, CheckCircleIcon, ArrowUturnLeftIcon,
  TicketIcon, PaperAirplaneIcon, EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import { ViewMode } from './AttendeeFilters';
import Dropdown from '../ui/Dropdown';
import ConfirmDialog from '../ui/ConfirmDialog';

interface AttendeeCardProps {
  registration: Registration;
  viewMode: ViewMode;
  onApprove?: () => void;
  onReject?: () => void;
  onCheckIn?: () => void;
  onNoShow?: () => void;
  onUndoNoShow?: () => void;
  onCheckOut?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onResendInvitation?: () => void;
  onResendVerification?: () => void;
  onResendTicket?: () => void;
  onSendSocialCard?: () => void;
  onShowHistory?: () => void;
  onManualApprove?: () => void;
  onCancelTicket?: () => void;
  onReinstate?: () => void;
}

const AttendeeCard: React.FC<AttendeeCardProps> = ({
  registration,
  viewMode,
  onApprove,
  onReject,
  onCheckIn,
  onNoShow,
  onUndoNoShow,
  onCheckOut,
  onDelete,
  onEdit,
  onResendInvitation,
  onResendVerification,
  onResendTicket,
  onSendSocialCard,
  onShowHistory,
  onManualApprove,
  onCancelTicket,
  onReinstate,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const getStatusColor = (status: string, isApproved: boolean) => {
    if (isApproved) return 'bg-green-50 border-green-200';
    
    switch (status) {
      case 'invited': return 'bg-blue-50 border-blue-200';
      case 'opened': return 'bg-indigo-50 border-indigo-200';
      case 'pending': return 'bg-yellow-50 border-yellow-200';
      case 'waitlist': return 'bg-orange-50 border-orange-200';
      case 'registered': return 'bg-purple-50 border-purple-200';
      case 'rejected': return 'bg-red-50 border-red-200';
      case 'declined': return 'bg-gray-50 border-gray-200';
      case 'cancelled': return 'bg-gray-100 border-gray-300';
      case 'checked_in': return 'bg-green-100 border-green-300';
      case 'no_show': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusBadge = () => {
    if (registration.status === 'checked_in') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-600 text-white">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Checked In
        </span>
      );
    }
    
    if (registration.is_approved) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          <CheckIcon className="w-3 h-3 mr-1" />
          Approved
        </span>
      );
    }
    
    const statusConfig: Record<string, { color: string; label: string }> = {
      invited: { color: 'blue', label: 'Invited' },
      opened: { color: 'indigo', label: 'Opened' },
      pending: { color: 'yellow', label: 'Pending Verification' },
      waitlist: { color: 'orange', label: 'Waitlist' },
      registered: { color: 'purple', label: 'Registered' },
      rejected: { color: 'red', label: 'Rejected' },
      declined: { color: 'gray', label: 'Declined' },
      cancelled: { color: 'gray', label: 'Cancelled' },
      no_show: { color: 'red', label: 'No-show' },
    };
    
    const config = statusConfig[registration.status];
    if (!config) return null;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        {config.label}
      </span>
    );
  };

  return (
    <div
      className={`border rounded-lg p-4 mb-3 ${getStatusColor(registration.status, registration.is_approved)}`}
    >
      {/* Main content area */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h3 className="font-medium text-gray-900">
              {registration.name || `${registration.given_name || ''} ${registration.family_name || ''}`.trim() || 'No name'}
            </h3>
            {getStatusBadge()}
            {registration.is_invitation_based && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                <EnvelopeIcon className="w-3 h-3 mr-1" />
                Invited
              </span>
            )}
            {/* Show verification status for registered users */}
            {(registration.status === 'pending' || registration.status === 'waitlist' || registration.is_approved) && (
              registration.email_verified ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  <CheckBadgeIcon className="w-3 h-3 mr-1" />
                  Verified
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  <XMarkIcon className="w-3 h-3 mr-1" />
                  Unverified
                </span>
              )
            )}
            {registration.reconfirmed_at && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800"
                title={`Confirmed coming ${format(new Date(registration.reconfirmed_at), 'MMM d, h:mm a')}`}
              >
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Confirmed coming
              </span>
            )}
          </div>
          
          <div className="text-sm text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-1 mb-2">
            <div>{registration.email}</div>
            {registration.company && <div>Company: {registration.company}</div>}
            {registration.role && <div>Role: {registration.role}</div>}
            {registration.relationship_type && <div>Type: {registration.relationship_type}</div>}
            {registration.linkedin_url && !/linkedin\.com\//i.test(registration.linkedin_url) && (
              <div>LinkedIn: {registration.linkedin_url}</div>
            )}
            {registration.linkedin_url && /linkedin\.com\//i.test(registration.linkedin_url) && (
              <div>
                <a
                  href={/^https?:\/\//i.test(registration.linkedin_url) ? registration.linkedin_url : `https://${registration.linkedin_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline"
                  title={registration.linkedin_url}
                >
                  <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
                  </svg>
                  LinkedIn profile
                </a>
              </div>
            )}
            {registration.dietary_requirements && <div>Dietary: {registration.dietary_requirements}</div>}
            {registration.non_dietary_requirements && <div>Other: {registration.non_dietary_requirements}</div>}
            {registration.how_heard && <div>Heard via: {registration.how_heard}</div>}
            {registration.notes && <div className="col-span-2 italic">Notes: {registration.notes}</div>}
          </div>
          
          <div className="text-xs text-gray-500">
            {registration.created_at && (
              <span>Requested: {format(new Date(registration.created_at), 'MMM d, yyyy h:mm a')}</span>
            )}
            {registration.checked_in_at && (
              <span className="ml-4">Checked in: {format(new Date(registration.checked_in_at), 'h:mm a')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Smart action buttons - minimal with dropdown menu */}
      <div className="flex items-center justify-end gap-2 py-3 mt-3 border-t border-gray-200">
        {/* Primary Actions - only the most critical ones */}
        {registration.status === 'waitlist' ? (
          <button
            onClick={onApprove}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <CheckIcon className="h-4 w-4" />
            Approve
          </button>
        ) : viewMode === 'checkin' && registration.is_approved && registration.status !== 'checked_in' ? (
          <button
            onClick={onCheckIn}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Check In
          </button>
        ) : viewMode === 'checkin' && registration.status === 'checked_in' ? (
          <button
            onClick={onCheckOut}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <ArrowUturnLeftIcon className="h-4 w-4" />
            Check Out
          </button>
        ) : null}

        {/* More Options Dropdown - contains all actions */}
        <Dropdown
          align="right"
          trigger={
            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
          }
          items={[
            {
              label: 'Edit',
              onClick: () => onEdit && onEdit(),
              icon: <PencilIcon className="h-4 w-4" />
            },
            {
              label: 'View History',
              onClick: () => onShowHistory && onShowHistory(),
              icon: <ClockIcon className="h-4 w-4" />
            },
            // Attendance actions (available in every view). None of these send email.
            ...((registration.status === 'approved' || registration.status === 'no_show') ? [{
              label: 'Check In',
              onClick: () => onCheckIn && onCheckIn(),
              icon: <CheckCircleIcon className="h-4 w-4" />,
              className: 'text-green-700 hover:bg-green-50'
            }] : []),
            ...(registration.status === 'checked_in' ? [{
              label: 'Undo Check In',
              onClick: () => onCheckOut && onCheckOut(),
              icon: <ArrowUturnLeftIcon className="h-4 w-4" />
            }] : []),
            ...(registration.status === 'approved' ? [{
              label: 'Mark No-Show',
              onClick: () => onNoShow && onNoShow(),
              icon: <XMarkIcon className="h-4 w-4" />,
              className: 'text-red-600 hover:bg-red-50'
            }] : []),
            ...(registration.status === 'no_show' ? [{
              label: 'Undo No-Show',
              onClick: () => onUndoNoShow && onUndoNoShow(),
              icon: <ArrowUturnLeftIcon className="h-4 w-4" />
            }] : []),
            ...(registration.status === 'waitlist' ? [{
              label: 'Reject',
              onClick: () => onReject && onReject(),
              icon: <XMarkIcon className="h-4 w-4" />,
              className: 'text-red-600 hover:bg-red-50'
            }] : []),
            { divider: true },
            // Email-related actions
            ...((registration.status === 'invited' || registration.status === 'opened') ? [
              {
                label: 'Manually Approve',
                onClick: () => onManualApprove && onManualApprove(),
                icon: <CheckIcon className="h-4 w-4" />,
                className: 'text-green-600 hover:bg-green-50'
              },
              {
                label: 'Resend Invitation',
                onClick: () => onResendInvitation && onResendInvitation(),
                icon: <EnvelopeIcon className="h-4 w-4" />,
                className: 'text-purple-600 hover:bg-purple-50'
              }
            ] : []),
            ...((registration.status === 'pending' || registration.status === 'waitlist') && !registration.email_verified ? [
              {
                label: 'Send Verification Email',
                onClick: () => onResendVerification && onResendVerification(),
                icon: <EnvelopeIcon className="h-4 w-4" />,
                className: 'text-yellow-600 hover:bg-yellow-50'
              },
              {
                label: 'Manually Approve',
                onClick: () => onManualApprove && onManualApprove(),
                icon: <CheckIcon className="h-4 w-4" />,
                className: 'text-green-600 hover:bg-green-50'
              }
            ] : []),
            ...(registration.is_approved ? [
              {
                label: 'Send Ticket',
                onClick: () => onResendTicket && onResendTicket(),
                icon: <TicketIcon className="h-4 w-4" />
              },
              {
                label: 'Send Social Card',
                onClick: () => onSendSocialCard && onSendSocialCard(),
                icon: <PaperAirplaneIcon className="h-4 w-4" />
              }
            ] : []),
            { divider: true },
            ...(registration.is_approved && registration.status !== 'cancelled' ? [{
              label: 'Cancel Ticket',
              onClick: () => onCancelTicket && onCancelTicket(),
              icon: <XMarkIcon className="h-4 w-4" />,
              className: 'text-orange-600 hover:bg-orange-50'
            }] : []),
            ...(registration.status === 'cancelled' ? [{
              label: 'Reinstate Ticket',
              onClick: () => onReinstate && onReinstate(),
              icon: <ArrowUturnLeftIcon className="h-4 w-4" />,
              className: 'text-green-600 hover:bg-green-50'
            }] : []),
            {
              label: 'Delete',
              onClick: () => setShowDeleteConfirm(true),
              icon: <TrashIcon className="h-4 w-4" />,
              className: 'text-red-600 hover:bg-red-50'
            }
          ]}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Registration"
        message={`Are you sure you want to delete the registration for ${registration.name || registration.email}? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (onDelete) onDelete();
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />
    </div>
  );
};

export default AttendeeCard;